<template>
  <div class="flex flex-row flex-wrap w-10">
    <HeadTitle
      title="Detalle de grupo de documentos"
      description="En esta sección puedes visualizar y editar la información de un grupo de documentos"
    />
    <EditRecordButton @update:is-edit-mode="toggleEditMode" />
  </div>
  <br />
  <Step :step-number="1" title="Alcance del grupo de documentos">
    <div class="flex flex-column">
      <MultiSelectDialog
        header-title="Seleccionar nóminas"
        label="Seleccionar nóminas"
        @update:visible="updateDialogVisibility"
        :options="payrolls"
        v-model="selectedPayrolls"
        :disabled="!isEditMode"
        show-key="name"
        value-key="id"
        :required="true"
      />
      <small id="text-error" class="p-error">{{
        payrollsError || '&nbsp;'
      }}</small>
    </div>
  </Step>
  <Divider />
  <Step :step-number="2" title="Documentos y políticas">
    <DataTable
      v-model:editingRows="editingRows"
      :value="documentSteps.data"
      editMode="row"
      dataKey="id"
      @row-edit-save="onRowEditSave"
      tableClass="editable-cells-table"
      tableStyle="min-width: 30rem"
      scrollHeight="600px"
      scrollable
      class="tabla"
    >
      <Column header="#" style="width: 10%">
        <template #body="{ data, index }">
          <Chip
            class="flex justify-content-center w-5 h-2rem"
            :class="{ 'bg-teal-200': data.file !== '' }"
            :label="String(index + 1)"
          />
        </template>
      </Column>
      <Column field="name" header="Nombre" style="width: 20%">
        <template #body="{ data }">
          {{ data.name }}
        </template>
      </Column>
      <Column field="type" header="Extensión" style="width: 10%">
        <template #body="{ data }">
          <span v-if="data.type">
            {{ data.type }}
          </span>
          <span v-else>
            <label class="text-600 font-medium">Sin asignar</label>
          </span>
        </template>
      </Column>
      <Column field="size" header="Tamaño(kb)" style="max-width: 5%">
        <template #body="{ data, field }">
          <span class="text-green-800">
            {{ Number(data[field] / 1024).toFixed(2) }}
          </span>
        </template>
      </Column>
      <Column field="file" header="Archivo" style="width: 10%">
        <template #body="{ data }">
          <Button
            v-if="data.file"
            v-tooltip.bottom="'ver adjunto'"
            class="action-btn view hover:text-blue-800 mr-2"
            severity="primary"
            icon="pi pi-paperclip"
            @click="downloadAttachment(data.file)"
          ></Button>
        </template>
        <template #editor="{ index }">
          <FileUpload
            mode="basic"
            class="font-light w-12 text-center text-sm"
            custom-upload
            @uploader="removeFile(index)"
            @select="saveFile($event, index)"
            size="small"
          />
        </template>
      </Column>
      <Column field="link_1" header="Enlace 1">
        <template #body="{ data }">
          <Button
            v-tooltip.bottom="data.link_1"
            v-if="data.link_1"
            icon="pi pi-external-link"
            class="action-btn view hover:text-blue-800"
            severity="secondary"
            @click="openLink(data.link_1)"
          ></Button>
        </template>
        <template #editor="{ data, field }">
          <InputText v-model="data[field]"></InputText>
        </template>
      </Column>
      <Column field="link_2" header="Enlace 2">
        <template #body="{ data }">
          <Button
            v-tooltip.bottom="data.link_2"
            v-if="data.link_2"
            icon="pi pi-external-link"
            class="action-btn view hover:text-blue-800"
            severity="secondary"
            @click="openLink(data.link_2)"
          ></Button>
        </template>
        <template #editor="{ data, field }">
          <InputText v-model="data[field]"></InputText>
        </template>
      </Column>
      <Column
        :rowEditor="true"
        style="width: 10%; min-width: 8rem"
        bodyStyle="text-align:center"
        v-if="isEditMode"
      >
      </Column>
      <Column v-if="isEditMode">
        <template #body="{ data, index }">
          <div class="actions">
            <Button
              class="action-btn view p-button-sm"
              icon="pi pi-trash"
              @click="deleteApprovalStep($event, data)"
              severity="danger"
              v-tooltip.bottom="'Eliminar'"
              :disabled="documentSteps.data.length === index + 1"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </Step>
  <Divider></Divider>
  <Step :step-number="3" title="Descripción del grupo de documentos">
    <div class="flex flex-column md:w-5 h-5">
      <Textarea
        class="mt-2"
        style="min-width: 250px"
        v-model="description"
        :disabled="!isEditMode"
      />
      <small id="text-error" class="p-error">{{
        descriptionError || '&nbsp;'
      }}</small>
    </div>
  </Step>
  <Footer>
    <template #left>
      <AuditButton
        :root-id="documentGroupId"
        :record-ids="[documentGroupId]"
        :entities="['Document', 'DocumentGroup']"
      />
    </template>
    <Button
      label="Cancelar"
      class="mr-2 text-sm font-light"
      severity="secondary"
      @click="
        router.push('/payrollsConfig/' + payrollConfigId + '/documentGroups')
      "
    />
    <Button
      label="Guardar"
      class="text-sm font-light"
      severity="primary"
      @click="onSave"
      v-show="isEditMode"
      :disabled="!isValidToSave"
    />
  </Footer>
</template>
<script setup lang="ts">
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import Footer from '@/shared/components/Footer.vue'
  import Step from '@/shared/components/Step.vue'
  import * as yup from 'yup'
  import MultiSelectDialog from '@/shared/components/MultiSelectDialog.vue'
  import { getPayrollsByConfigId } from '@/modules/payrolls-config/services/PayrollService'
  import { useField } from 'vee-validate'
  import useNotifications from '@/store/notification'
  import router from '@/router'
  import useLoading from '@/store/load'
  import DocumentFile from '../models/DocumentFile'
  import { getFileUrlPresigned } from '@/shared/services/fileService'
  import {
    getDocumentGroupsByPayrollConfigId,
    uploadDocumentService,
    deleteDocumentService,
    updateDocumentGroup,
    updateDocuments,
  } from '../services/DocumentService'
  import UploadDocumentDto from '../services/dtos/UploadDocumentDto'
  import DocumentGroup from '../models/DocumentGroup'
  import UpdateDocumentDto from '../services/dtos/UpdateDocumentDto'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import EditRecordButton from '@/shared/components/EditRecordButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'

  const path = useRoute()
  const payrollConfigId = ref()
  const notificationStore = useNotifications()
  const loadingStore = useLoading()

  const payrolls = ref()
  const dialogVisible = ref(false)
  const isEditMode = ref(false)
  const documentGroupId = ref<string>('')
  const documentGroup = ref<DocumentGroup>()
  const documentsToDelete = ref<string[]>([])
  const documentsToUpdate = ref<UpdateDocumentDto[]>([])
  const documentsToCreate = ref<UploadDocumentDto[]>([])

  //variables used in the second step
  const documentSteps = reactive({
    data: [
      {
        type: '',
        name: '',
        size: '',
        file: '',
        link_1: '',
        link_2: '',
        id: '',
      },
    ] as DocumentFile[],
    totalItems: 1,
  })

  watch(isEditMode, () => {
    const lastStep = documentSteps.data[documentSteps.data.length - 1]
    const lastStepHasFileOrLink =
      lastStep.file !== '' || lastStep.link_1 !== '' || lastStep.link_2 !== ''
    if (isEditMode && lastStepHasFileOrLink) {
      documentSteps.data.push({
        id: '1',
        type: '',
        name: '',
        size: '',
        file: '',
        link_1: '',
        link_2: '',
      })
    } else {
      documentSteps.data.pop()
    }
  })

  const descriptionSchema = yup
    .string()
    .required('La descripción del flujo es requerida')
  const payrollsSchema = yup
    .array()
    .min(1, 'Al menos una nómina debe ser seleccionado')
    .required('Las nóminas son requeridas')
  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: selectedPayrolls,
    errorMessage: payrollsError,
    validate: payrollsValidate,
  } = useField<string[]>('selectedPayrolls', payrollsSchema)
  const editingRows = ref([])

  onMounted(async () => {
    loadingStore.setLoadingState()
    payrollConfigId.value = path.params.id
    documentGroupId.value = String(path.params.documentGroupId)
    const documentGroupsResponse = await getDocumentGroupsByPayrollConfigId(
      String(payrollConfigId.value)
    )

    documentGroup.value = documentGroupsResponse.data.find(
      (dg) => dg.id === documentGroupId.value || null
    )

    if (documentGroup.value) {
      documentSteps.data = documentGroup.value?.documents.map((df, index) => ({
        type: df.type,
        name: df.name,
        size: df.size,
        file: df.file,
        link_1: df.link_1,
        link_2: df.link_2,
        createdAt: df.createdAt,
        updatedAt: df.updatedAt,
        id: df.id ? df.id : String(index + 1),
      }))

      description.value = documentGroup.value.description
      selectedPayrolls.value = documentGroup.value.onlyPayrolls
    }

    const payrollReponse = await getPayrollsByConfigId(
      String(payrollConfigId.value)
    )
    payrolls.value = payrollReponse.map((payroll) => ({
      id: payroll.id,
      name: payroll.code + '-' + payroll.name,
    }))

    loadingStore.dismissLoadingState()
  })

  const updateDialogVisibility = (value: boolean) => {
    dialogVisible.value = value
  }

  const toggleEditMode = (value: boolean) => {
    isEditMode.value = value
  }

  const saveFile = (event: any, index: number) => {
    const file = event.files[0]
    documentSteps.data[index].file = file
    documentSteps.data[index].type = file.type.split('/')[1]
    documentSteps.data[index].name = file.name
    documentSteps.data[index].size = file.size
  }

  const removeFile = (index: number) => {
    documentSteps.data[index].file = ''
  }

  const deleteApprovalStep = (event: any, step: DocumentFile) => {
    if (isNaN(Number(step.id))) {
      documentsToDelete.value.push(String(step.id))
    }
    documentSteps.data = documentSteps.data.filter((ds) => ds.id !== step.id)
  }

  const onSave = async () => {
    loadingStore.setLoadingState()
    const isValid = (await descValidate()) && (await payrollsValidate())

    if (documentSteps.data.length === 1) {
      notificationStore.addErrorNotification({
        detail: `Debe agregar al menos un documento`,
        severity: 'error',
        summary: `Error al actualizar el grupo de documentos`,
      })
      return
    }

    await updateDocumentGroup(documentGroupId.value || '', {
      description: description.value,
      onlyPayrolls: selectedPayrolls.value,
      payrollConfigId: payrollConfigId.value,
    })

    //Delete last step because is empty
    documentSteps.data.pop()
    documentSteps.data.forEach((step) => {
      if (isNaN(Number(step.id))) {
        documentsToUpdate.value.push({
          groupId: documentGroupId.value || '',
          documentId: step.id || '',
          link_1: step.link_1,
          link_2: step.link_2,
          file: step.file instanceof File ? step.file : undefined,
        })
      } else {
        documentsToCreate.value.push({
          file: step.file instanceof File ? step.file : undefined,
          groupId: documentGroupId.value || '',
          link_1: step.link_1,
          link_2: step.link_2,
        })
      }
    })

    let stepsUdpdatedResponse
    let stepsCreatedResponse
    let stepsToDeleteResponse

    if (documentsToUpdate.value.length > 0) {
      //UPDATE DOCUMENTS
      stepsUdpdatedResponse = await updateDocuments(documentsToUpdate.value)
    }

    if (documentsToCreate.value.length > 0) {
      //CREATE DOCUMENTS
      stepsCreatedResponse = await uploadDocumentService(
        documentsToCreate.value
      )
    }

    if (documentsToDelete.value.length > 0) {
      //DELETE DOCUMENTS
      stepsToDeleteResponse = await deleteDocumentService(
        documentsToDelete.value
      )
    }

    loadingStore.dismissLoadingState()
    if (
      stepsUdpdatedResponse ||
      stepsCreatedResponse ||
      stepsToDeleteResponse
    ) {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Grupo de documento actualizado con exito`,
      })

      router.push(
        '/payrollsConfig/' + payrollConfigId.value + '/documentGroups'
      )
    }
  }

  const downloadAttachment = async (file: string | File) => {
    if (typeof file === 'string') {
      const fileUrl = await getFileUrlPresigned(file)
      window.open(fileUrl, '_blank')
    } else {
      //visualize File type
      const fileUrl = URL.createObjectURL(file)
      window.open(fileUrl, '_blank')
    }
  }

  const isValidToSave = computed(() => {
    return documentSteps.data.length > 1 && description.value
  })

  const onRowEditSave = (event: any) => {
    let { newData, index } = event

    documentSteps.data[index] = {
      type: documentSteps.data[index].type,
      name: documentSteps.data[index].name,
      size: documentSteps.data[index].size,
      file: documentSteps.data[index].file,
      link_1: newData.link_1,
      link_2: newData.link_2,
      id: documentSteps.data[index].id
        ? documentSteps.data[index].id
        : String(index + 1),
    }

    newData = documentSteps.data[index]

    if (
      (newData.link_1 !== '' || newData.link_2 !== '' || newData.file !== '') &&
      index === documentSteps.data.length - 1
    ) {
      const newStepNumber = Number(documentSteps.data[index].id) + 1
      documentSteps.data.push({
        id: String(newStepNumber),
        type: '',
        name: '',
        size: '',
        file: '',
        link_1: '',
        link_2: '',
      })
    }
    return
  }

  const openLink = (link: string) => {
    window.open(link, '_blank')
  }
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  .actions {
    display: flex;
    max-width: 180px;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    margin-right: 10px;
  }
</style>
